import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { markOnboardingSeen } from '../../lib/onboardingState';
import { ONBOARDING_SLIDES } from '../../lib/onboardingSlides';
import { isNative } from '../../lib/platform';
import { AmbientVideo } from '../design-system';

interface Persona {
  name: string;
  avatar_url: string | null;
  video_url: string | null;
}

interface OnboardingCarouselProps {
  country: string | null;
  /** Called once the tour is exited (skip, or navigating away for auth). */
  onDismiss: () => void;
}

const KEYFRAMES = `
@keyframes sk-onb-up{from{transform:translateY(40px);opacity:0}to{transform:translateY(0);opacity:1}}
@keyframes sk-onb-fade{from{opacity:0}to{opacity:1}}
`;

/** Renders `\n` in slide copy as manual `<br/>` line breaks. */
function renderWithBreaks(text: string) {
  const lines = text.split('\n');
  return lines.map((line, i) => (
    <span key={i}>
      {line}
      {i < lines.length - 1 && <br />}
    </span>
  ));
}

/**
 * OnboardingCarousel — the pre-auth /try entry gate. Visually modeled on
 * the design-system IntroModal (dark overlay, square 1:1 media area, serif
 * title, hairline, pill CTA) that TryIntroModal already used, extended with
 * paging across 4 slides. The final slide hands off to /register (which
 * owns the actual Google/email choice) rather than duplicating it here.
 */
export function OnboardingCarousel({ country, onDismiss }: OnboardingCarouselProps) {
  const navigate = useNavigate();
  const [persona, setPersona] = useState<Persona | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Mirrors activeIndex but only updates once the swipe/scroll has actually
  // settled — gates the bottom CTA section so its layout (padding, button
  // count) doesn't swap mid-animation while slide 4 is still sliding in.
  const [settledIndex, setSettledIndex] = useState(0);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout>>();

  const lastIndex = ONBOARDING_SLIDES.length - 1;
  const isLast = settledIndex === lastIndex;
  // On the native mobile app, login/signup must come first — the tour can't
  // be skipped, only completed through to the "Get started" CTA.
  const allowSkip = !isNative;

  useEffect(() => {
    async function load() {
      let matched: Persona | null = null;

      if (country) {
        const { data } = await supabase
          .from('ai_persona_countries')
          .select('persona_id')
          .eq('country', country)
          .limit(1)
          .single();
        if (data) {
          const { data: p } = await supabase
            .from('ai_personas')
            .select('name, avatar_url, video_url')
            .eq('id', data.persona_id)
            .eq('active', true)
            .single();
          if (p) matched = p;
        }
      }

      if (!matched) {
        const { data: p } = await supabase
          .from('ai_personas')
          .select('name, avatar_url, video_url')
          .eq('active', true)
          .order('created_at', { ascending: true })
          .limit(1)
          .single();
        if (p) matched = p;
      }

      setPersona(matched);
    }
    void load();
  }, [country]);

  useEffect(() => {
    return () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
  }, []);

  function goTo(index: number) {
    const clamped = Math.max(0, Math.min(lastIndex, index));
    setActiveIndex(clamped);
    const el = scrollerRef.current;
    if (el) el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
  }

  function handleScroll() {
    const el = scrollerRef.current;
    if (!el || el.clientWidth === 0) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    if (index !== activeIndex) setActiveIndex(index);

    // Debounce: only commit settledIndex once scroll position stops
    // changing, so the bottom CTA section swaps after the slide has
    // actually finished sliding into view, not mid-animation.
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => setSettledIndex(index), 60);
  }

  function skip() {
    markOnboardingSeen();
    onDismiss();
  }

  function handleGetStarted() {
    markOnboardingSeen();
    // No exit animation needed here — /register's own shell fades in on top
    // (see AuthLayout), so this card can just sit still and get covered
    // rather than needing its own choreographed exit.
    onDismiss();
    navigate('/register');
  }

  const media = (index: number) => {
    if (index === 0) {
      if (persona?.video_url) {
        return (
          <AmbientVideo
            src={persona.video_url}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        );
      }
      if (persona?.avatar_url) {
        return (
          <img
            src={persona.avatar_url}
            alt={persona.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        );
      }
      return null;
    }
    const slide = ONBOARDING_SLIDES[index];
    if (slide.image) {
      return (
        <img
          src={slide.image}
          alt={slide.title}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      );
    }
    return slide.icon ? (
      <div className="w-full h-full flex items-center justify-center">
        <i className={`ti ti-${slide.icon} text-6xl`} style={{ color: 'var(--accent)' }} />
      </div>
    ) : null;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 backdrop-blur-md"
      style={{ background: 'rgba(0,0,0,.55)', animation: 'sk-onb-fade .2s ease both' }}
    >
      <style>{KEYFRAMES}</style>
      <div
        className="w-full bg-app-card border border-app-border overflow-hidden max-h-dvh-92 overflow-y-auto"
        style={{ maxWidth: 380, borderRadius: 18, animation: 'sk-onb-up .3s cubic-bezier(.16,.8,.3,1) both' }}
      >
        {/* Media + text — one swipeable unit per slide */}
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {ONBOARDING_SLIDES.map((slide, i) => (
            <div key={slide.title} className="w-full shrink-0 snap-center" style={{ maxWidth: 380 }}>
              <div className="relative w-full bg-app-bg" style={{ aspectRatio: '1 / 1' }}>
                {allowSkip && (
                  <button
                    type="button"
                    onClick={skip}
                    aria-label="Skip"
                    className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,.32)' }}
                  >
                    <i className="ti ti-x text-base" style={{ color: 'rgb(var(--cream))' }} />
                  </button>
                )}
                {media(i)}
              </div>

              <div className="px-6 pt-[22px] pb-1">
                <div className="flex items-center gap-3">
                  <span
                    className="font-serif shrink-0"
                    style={{ fontSize: 68, lineHeight: 1, letterSpacing: '-0.04em', color: 'var(--text)', opacity: 0.15 }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h2 className="font-serif text-app-fg" style={{ fontSize: 32, lineHeight: 1.06 }}>
                    {renderWithBreaks(slide.title)}
                  </h2>
                </div>
                <div className="h-px bg-app-border my-3.5" />
                <p
                  className={`text-app-fg-2 text-sm leading-relaxed min-h-[4.5rem] max-w-[320px] ${slide.body.includes('\n') ? '' : 'text-balance'}`}
                >
                  {renderWithBreaks(slide.body)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 pb-6 pt-3">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            {ONBOARDING_SLIDES.map((slide, i) => (
              <button
                key={slide.title}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className="p-1.5 -m-1.5"
              >
                <span
                  className="block w-1.5 h-1.5 rounded-full transition-colors"
                  style={{ background: i === activeIndex ? 'var(--accent)' : 'var(--line)' }}
                />
              </button>
            ))}
          </div>

          {isLast ? (
            <button
              onClick={handleGetStarted}
              className="w-full bg-app-fg text-app-bg rounded-pill py-3.5 text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all"
            >
              Get started
            </button>
          ) : (
            <button
              onClick={() => goTo(activeIndex + 1)}
              className="w-full bg-app-fg text-app-bg rounded-pill py-3.5 text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingCarousel;
